import { captureException } from '@sentry/react-router'
import { useEffect, type ReactElement } from 'react'
import {
	type ErrorResponse,
	Link,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import logoAssetUrl from '../assets/logo.svg'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<>
			<p className="text-muted-foreground mb-2 max-w-md font-mono">
				We encountered an error ({error.status}) while processing your request.
			</p>
		</>
	),
	statusHandlers,
	unexpectedErrorHandler = () => (
		<>
			<p className="text-muted-foreground mb-2 max-w-md font-mono">
				An unexpected error occurred while loading this page.
			</p>
		</>
	),
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const error = useRouteError()
	const params = useParams()
	const isResponse = isRouteErrorResponse(error)

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	useEffect(() => {
		if (isResponse) return

		captureException(error)
	}, [error, isResponse])

	const errorContent = isResponse
		? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
				error,
				params,
			})
		: unexpectedErrorHandler(error)

	return (
		<div className="container flex items-center justify-center">
			<div className="flex min-h-[60vh] flex-col items-center justify-center p-8 text-center">
				<img src={logoAssetUrl} alt="xcelera" className="mb-6 size-16" />
				<h2 className="mb-4 font-mono text-2xl">Something bad happened :(</h2>
				{errorContent}
				<p className="text-muted-foreground mb-4 max-w-md text-sm">
					We have already been notified of this error. If you want to discuss,
					please{' '}
					<Link
						className="text-primary underline hover:no-underline"
						to="/support"
					>
						get in touch
					</Link>
					.
				</p>
				<Button
					onClick={() => window.location.reload()}
					variant="default"
					size="lg"
					className="font-mono"
				>
					Reload
				</Button>
			</div>
		</div>
	)
}
